import type { MenuProps } from 'antd';
import type { SubMenuType } from 'antd/es/menu/interface';

import {
  AlertOutlined,
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  FlagOutlined,
  FormOutlined,
  HomeOutlined,
  HourglassOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { AppCode, Role } from '../../enum';
import { validateAsAdmin, validateAsFirstAlertsRole, validateAsSPManagerRole } from '../../helpers/utils.helper';
import { isMobile } from '../../store';
import type { AuthResult, CurrentUser } from '../pages/Login/authSlice';

export const feedbackSubMenuChildren: MenuProps['items'] = [
  { label: 'Feedback from peers', key: '/advocate-feedback' },
  { label: 'Feedback from guests', key: '/guest-feedback' },
  { label: 'Reported guests', key: '/abusive-guests' },
  { label: 'Reported chat logs', key: '/reports-of-abuse' },
];

export const adminSubMenuChildren: MenuProps['items'] = [
  { label: 'Peer evaluation', key: '/peer-eval-dashboard' },
  { label: 'Peers', key: '/admin-advocates' },
  { label: 'Managers', key: '/admin-managers' },
  { label: 'Service providers', key: '/admin-service-providers' },
  { label: 'Service provider managers', key: '/admin-service-providers-managers' },
  { label: 'Service provider organizations', key: '/admin-service-provider-organizations' },
  { label: 'Cohorts', key: '/admin-cohorts' },
  { label: 'Unlock codes', key: '/admin-unlock-codes' },
  { label: 'Peer resources', key: '/admin-advocate-resources' },
  { label: 'Peer hours', key: '/admin-advocates-hours' },
  { label: 'Admin accounts', key: '/super-admins' },
  { label: 'Organizations', key: '/orgs' },
];

const addServiceProvider: NonNullable<MenuProps['items']>[number] = {
  label: 'Add service provider',
  icon: <UserAddOutlined />,
  key: '/service-provider-onboarding',
};

const serviceProviderManagerItems: MenuProps['items'] = [
  {
    label: 'Talking Stick dashboard',
    icon: <HomeOutlined />,
    key: '/program-dashboard-ts',
  },
  {
    label: 'Demographic insights',
    icon: <UserOutlined />,
    key: '/preferences-dashboard',
  },
  {
    label: 'Service providers online',
    icon: <CheckCircleOutlined />,
    key: '/service-providers-online-dashboard',
  },
  {
    label: 'Service providers',
    icon: <UsergroupAddOutlined />,
    key: '/admin-service-providers',
  },
  { label: 'Managers', icon: <UserOutlined />, key: '/admin-service-providers-managers' },
  { label: 'Service provider hours', icon: <HourglassOutlined />, key: '/admin-advocates-hours' },
  {
    label: 'Feedback',
    icon: <FormOutlined />,
    key: 'FeedbackSubMenu',
    children: [
      { label: 'Feedback from service providers', key: '/advocate-feedback' },
      { label: 'Feedback from guests', key: '/guest-feedback' },
    ],
  },
  addServiceProvider,
];

export const firstAlertsItems: MenuProps['items'] = [
  { label: 'Control panel', icon: <BellOutlined />, key: '/alerts' },
  { label: 'Users', icon: <UserOutlined />, key: '/admin-first-alerts-managers' },
  { label: 'Communities', icon: <FlagOutlined />, key: '/first-alerts-communities' },
];

const generalItems: MenuProps['items'] = [
  {
    label: 'Talking Stick dashboard',
    icon: <HomeOutlined />,
    key: '/program-dashboard-ts',
  },
  {
    label: "Buddy's Quest dashboard",
    icon: <HomeOutlined />,
    key: '/program-dashboard-bq',
  },
  {
    label: 'Demographic insights',
    icon: <UserOutlined />,
    key: '/preferences-dashboard',
  },
  {
    label: 'Peers online',
    icon: <CheckCircleOutlined />,
    key: '/advocates-online-dashboard',
  },
  {
    label: 'Service providers online',
    icon: <CheckCircleOutlined />,
    key: '/service-providers-online-dashboard',
  },
  { label: 'Peer hours', icon: <HourglassOutlined />, key: '/admin-advocates-hours' },
  {
    label: 'Downloads',
    icon: <DownloadOutlined />,
    key: '/downloads',
  },
  {
    label: 'Peers',
    icon: <UsergroupAddOutlined />,
    key: '/advocates',
  },
  {
    label: 'Admin',
    icon: <UserOutlined />,
    key: 'AdminSubMenu',
  },
  { label: 'Communities', icon: <FlagOutlined />, key: '/admin-communities' },
  {
    label: 'Feedback',
    icon: <FormOutlined />,
    key: 'FeedbackSubMenu',
  },
  { label: 'Add peer', icon: <UserAddOutlined />, key: '/advocate-onboarding' },
  addServiceProvider,
  { label: 'Timesheets', icon: <ClockCircleOutlined />, key: '/timesheets' },
  {
    label: 'FirstAlerts',
    icon: <AlertOutlined />,
    key: 'FirstAlertsSubMenu',
  },
];

const userAccountItems: MenuProps['items'] = [
  {
    label: 'My settings',
    icon: <SettingOutlined />,
    key: '/manager-settings',
  },
  { label: 'Log out', icon: <LogoutOutlined />, key: '/logout' },
];

export const getMenuItems = (currentUser: CurrentUser, authResult: AuthResult) => {
  if (validateAsAdmin(currentUser.role)) {
    const items = generalItems.concat([...userAccountItems]);

    return items.filter((item) => {
      switch (item?.key) {
        case '/advocates':
        case '/admin-advocates-hours':
          return false;
        case 'FeedbackSubMenu':
          (item as SubMenuType).children = feedbackSubMenuChildren;
          return true;
        case 'FirstAlertsSubMenu':
          (item as SubMenuType).children = firstAlertsItems;
          return true;
        case 'AdminSubMenu':
          (item as SubMenuType).children = adminSubMenuChildren.filter((submenu) => {
            switch (submenu?.key) {
              case '/super-admins':
                return currentUser.role === Role.SUPER_ADMIN;
              case '/orgs':
                return currentUser.role === Role.SUPER_ADMIN;
              default:
                return true;
            }
          });
          return true;
        default:
          return true;
      }
    });
  }

  if (validateAsFirstAlertsRole(currentUser.role)) {
    return firstAlertsItems
      .filter((item) => {
        switch (item?.key) {
          case '/admin-first-alerts-managers':
            return currentUser.role === Role.ALERT_MANAGER;
          case '/first-alerts-communities':
            return false;
          case '/program-dashboard-bq':
            return false;
          default:
            return true;
        }
      })
      .concat(...userAccountItems);
  }

  if (validateAsSPManagerRole(currentUser.role)) {
    return serviceProviderManagerItems.concat(...userAccountItems);
  }

  if (currentUser.role === Role.DASHBOARD_ONLY) {
    return generalItems
      .filter((item) => {
        switch (item?.key) {
          case '/program-dashboard-ts':
            return currentUser.app_code === AppCode.TalkingStick;
          case '/program-dashboard-bq':
            return currentUser.app_code === AppCode.BuddysQuest;
          case '/preferences-dashboard':
            return currentUser.app_code !== AppCode.BuddysQuest;
          case '/advocates-online-dashboard':
            return true;
          default:
            return false;
        }
      })
      .concat(
        ...(currentUser.app_code === AppCode.BuddysQuest
          ? [
              {
                label: 'Unlock codes',
                icon: <TeamOutlined />,
                key: '/admin-unlock-codes',
              },
            ]
          : []),
      )
      .concat(...userAccountItems);
  }

  // Director or managers
  return generalItems
    .filter((item) => {
      switch (item?.key) {
        case '/program-dashboard-ts':
          return currentUser.app_code === AppCode.TalkingStick;
        case '/program-dashboard-bq':
          return currentUser.app_code === AppCode.BuddysQuest;
        case '/preferences-dashboard':
          return currentUser.app_code !== AppCode.BuddysQuest;
        case '/admin-advocates-hours':
        case '/advocates':
          return currentUser.role === Role.MANAGER;
        case '/service-providers-online-dashboard':
        case '/downloads':
        case '/service-provider-onboarding':
        case '/admin-communities':
        case '/timesheets':
          return false;
        case 'AdminSubMenu':
          (item as SubMenuType).children = adminSubMenuChildren.filter((c) => {
            switch (c?.key) {
              case '/admin-cohorts': //return currentUser.app_code === AppCode.BuddysQuest;
              case '/super-admins':
              case '/orgs':
              case '/admin-service-providers-managers':
              case '/admin-service-provider-organizations':
              case '/admin-service-providers':
              case '/peer-eval-dashboard':
                return false;
              case '/admin-unlock-codes':
                return currentUser.app_code === AppCode.BuddysQuest;
              case '/admin-advocate-resources':
                return currentUser.app_code !== AppCode.BuddysQuest;
              default:
                return true;
            }
          });
          return currentUser.role === Role.DIRECTOR && !isMobile;
        case 'FeedbackSubMenu':
          (item as SubMenuType).children = feedbackSubMenuChildren.filter((c) => {
            switch (c?.key) {
              case '/abusive-guests':
              case '/reports-of-abuse':
                return false;
              default:
                return true;
            }
          });
          return true;
        case 'FirstAlertsSubMenu':
          (item as SubMenuType).children = firstAlertsItems;
          return authResult.use_first_alerts && currentUser.role === Role.DIRECTOR;
        default:
          return true;
      }
    })
    .concat(...userAccountItems);
};
